// Pure functions, no Angular DI. Confirmed zero existing use of navigator.storage anywhere
// in this app — this is greenfield (docs/offline-mode-plan.md P4/P6).

export interface QuotaEstimate {
  usage: number;
  quota: number;
  freeBytes: number;
}

export async function estimateQuota(): Promise<QuotaEstimate | null> {
  if (!navigator.storage || !navigator.storage.estimate) return null;
  try {
    const est = await navigator.storage.estimate();
    const usage = est.usage ?? 0;
    const quota = est.quota ?? 0;
    return { usage, quota, freeBytes: Math.max(0, quota - usage) };
  } catch {
    return null;
  }
}

// headroom: don't run right up against the quota — leave room for browser/OS overhead and
// other origins' storage pressure on the same device.
export async function hasRoomFor(bytes: number, headroom = 50 * 1024 * 1024): Promise<boolean> {
  const estimate = await estimateQuota();
  if (!estimate) return true; // can't tell — don't block the download on an unknown
  return estimate.freeBytes >= bytes + headroom;
}

// Never call unprompted at app bootstrap, which would be a hostile permission-style request on
// page load for a feature the user hasn't touched yet — see ensurePersistence for the
// after-a-save caller. Best-effort: browsers may auto-grant, prompt, or silently ignore this.
export async function requestPersistence(): Promise<boolean> {
  if (!navigator.storage || !navigator.storage.persist) return false;
  try {
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}

// Asks for persistence only if it has not already been granted. Called automatically after a
// successful save (lessons and videos), so a student never has to find the manual button on
// the downloads page. Without it, browsers may evict downloads under storage pressure. Firefox
// shows a permission prompt the first time; a browser that already decided is not re-asked
// in a way that bothers the user. Never throws.
export async function ensurePersistence(): Promise<boolean> {
  try {
    if (navigator.storage?.persisted && (await navigator.storage.persisted())) return true;
  } catch {
    /* fall through and ask */
  }
  return requestPersistence();
}
